// Profile pictures (same flow as iOS ProfilePhoto.swift): crop to a 512px
// square, upload to profilePhotoUploads/{uid}.jpg, then wait for the
// onProfilePhotoUploaded function's verdict in photoReviews/{uid}. A clean
// photo is published to users/{uid}/photoURL; nobody sees it before that.

import { useEffect, useState } from 'react';
import { getDatabase, ref as dbRef, get, onValue, remove } from 'firebase/database';
import { getStorage, ref as fileRef, uploadBytes } from 'firebase/storage';

const SIZE = 512;
const TIMEOUT_MS = 45000;

const cache = new Map();   // uid -> url ('' if none)
const pending = new Map(); // uid -> Promise
const listeners = new Set();

function load(uid) {
  if (cache.has(uid)) return Promise.resolve(cache.get(uid));
  if (!pending.has(uid)) {
    pending.set(uid, get(dbRef(getDatabase(), `users/${uid}/photoURL`))
      .then(s => s.val() || '')
      .catch(() => '')
      .then(url => { cache.set(uid, url); pending.delete(uid); return url; }));
  }
  return pending.get(uid);
}

function setCached(uid, url) {
  cache.set(uid, url);
  listeners.forEach(fn => fn(uid));
}

/** A user's photo URL, or '' while loading or when they have none. */
export function usePhotoURL(uid) {
  const [url, setUrl] = useState(() => cache.get(uid) || '');
  useEffect(() => {
    if (!uid) return undefined;
    let live = true;
    load(uid).then(u => { if (live) setUrl(u); });
    const onChange = changed => { if (changed === uid) setUrl(cache.get(uid) || ''); };
    listeners.add(onChange);
    return () => { live = false; listeners.delete(onChange); };
  }, [uid]);
  return url;
}

/** Center-crops an image file to a JPEG square. */
function cropToSquare(file) {
  return new Promise((resolve, reject) => {
    const objectURL = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = SIZE;
      canvas.height = SIZE;
      canvas.getContext('2d').drawImage(img,
        (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, SIZE, SIZE);
      URL.revokeObjectURL(objectURL);
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('crop'))), 'image/jpeg', 0.8);
    };
    img.onerror = () => { URL.revokeObjectURL(objectURL); reject(new Error('Couldn’t open that photo.')); };
    img.src = objectURL;
  });
}

function waitForReview(uid) {
  return new Promise((resolve, reject) => {
    const reviewRef = dbRef(getDatabase(), `photoReviews/${uid}`);
    let unsubscribe = () => {};
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error('Checking your photo is taking too long. Please try again.'));
    }, TIMEOUT_MS);
    unsubscribe = onValue(reviewRef, snapshot => {
      const status = snapshot.val()?.status;
      if (!status) return;
      clearTimeout(timer);
      unsubscribe();
      if (status === 'ok') resolve();
      else if (status === 'rejected') reject(new Error('That photo can’t be used. Please choose a different one.'));
      else reject(new Error('We couldn’t check your photo. Please try again.'));
    });
  });
}

/** Uploads a photo and resolves once it has passed and been published. */
export async function uploadProfilePhoto(uid, file) {
  const blob = await cropToSquare(file);
  // Clear the last verdict first, so only this upload's result can answer.
  await remove(dbRef(getDatabase(), `photoReviews/${uid}`));
  await uploadBytes(fileRef(getStorage(), `profilePhotoUploads/${uid}.jpg`), blob, { contentType: 'image/jpeg' });
  await waitForReview(uid);
  const url = (await get(dbRef(getDatabase(), `users/${uid}/photoURL`))).val() || '';
  setCached(uid, url);
}

export async function removeProfilePhoto(uid) {
  await remove(dbRef(getDatabase(), `users/${uid}/photoURL`));
  setCached(uid, '');
}
